import {
  faBookOpen,
  faChartLine,
  faInbox,
  faKey,
  faLayerGroup,
  faRocket,
  type IconDefinition,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Box,
  Icon,
  Inline,
  Message,
  Section,
  Skeleton,
} from "@intility/bifrost-react";
import { Link } from "react-router";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { usePendingRequests } from "../../api/access";
import { useClients } from "../../api/clients";
import { usePlatforms } from "../../api/platforms";
import type { ClientListItem } from "../../api/types";
import heroFingerprintDark from "../../assets/hero-fingerprint-dark.svg";
import heroFingerprintLight from "../../assets/hero-fingerprint-light.svg";
import s from "./Dashboard.module.css";

const CHART_HEIGHT = 260;

const SERIES = {
  total: "All clients",
  agents: "AI agents",
  mcp: "MCP servers",
} as const;

function monthKey(date: Date): number {
  return date.getFullYear() * 12 + date.getMonth();
}

// Cumulative clients per month since the first registration, with AI agents
// and MCP servers broken out. Clients without a usable createdAt are counted
// from the first bucket so the final total still matches the stat tile.
function buildClientTrend(
  clients: Pick<ClientListItem, "purposeTag" | "createdAt">[],
) {
  if (clients.length === 0) return [];

  const parsedKeys = clients.map((c) => {
    const d = c.createdAt ? new Date(c.createdAt) : null;
    return d && !Number.isNaN(d.getTime()) ? monthKey(d) : null;
  });

  const currentKey = monthKey(new Date());
  const knownKeys = parsedKeys.filter((k): k is number => k !== null);
  const firstKey = knownKeys.length ? Math.min(...knownKeys) : currentKey;
  // One leading empty bucket so the lines rise from zero.
  const startKey = firstKey - 1;
  const bucketCount = currentKey - startKey + 1;

  const newTotals = new Array<number>(bucketCount).fill(0);
  const newAgents = new Array<number>(bucketCount).fill(0);
  const newMcp = new Array<number>(bucketCount).fill(0);

  clients.forEach((client, i) => {
    const key = Math.min(parsedKeys[i] ?? firstKey, currentKey);
    const idx = key - startKey;
    newTotals[idx] += 1;
    if (client.purposeTag === "ai-agent") newAgents[idx] += 1;
    if (client.purposeTag === "mcp-server") newMcp[idx] += 1;
  });

  const spansYears = Math.floor(startKey / 12) !== Math.floor(currentKey / 12);
  const monthFormat = new Intl.DateTimeFormat(
    "en",
    spansYears ? { month: "short", year: "2-digit" } : { month: "short" },
  );

  let total = 0;
  let agents = 0;
  let mcp = 0;
  return newTotals.map((_, idx) => {
    total += newTotals[idx];
    agents += newAgents[idx];
    mcp += newMcp[idx];
    const key = startKey + idx;
    return {
      name: monthFormat.format(new Date(Math.floor(key / 12), key % 12, 1)),
      [SERIES.total]: total,
      [SERIES.agents]: agents,
      [SERIES.mcp]: mcp,
    };
  });
}

function ChartTooltip({
  label,
  payload,
}: {
  label?: string;
  payload?: { name?: string; value?: number }[];
}) {
  if (!payload?.length) return null;
  return (
    <Box background radius shadow padding={14}>
      {label && <strong>{label}</strong>}
      {payload.map((entry) => (
        <div key={entry.name}>
          {entry.name}: <strong>{entry.value}</strong>
        </div>
      ))}
    </Box>
  );
}

function ChartCard({ children }: { children: React.ReactNode }) {
  return (
    <Section shadow={false} border radius>
      <Section.Header>
        <Inline align="center" gap={8}>
          <Icon icon={faChartLine} className="bfc-base-2" />
          <Inline.Stretch>Clients over time</Inline.Stretch>
        </Inline>
      </Section.Header>
      <Section.Content padding={false} className={s.chartContent}>
        {children}
      </Section.Content>
    </Section>
  );
}

function ClientsOverTimeChart({
  clients,
}: {
  clients?: Pick<ClientListItem, "purposeTag" | "createdAt">[];
}) {
  if (!clients) {
    return (
      <ChartCard>
        <Skeleton>
          <Skeleton.Rect width="100%" height={CHART_HEIGHT} />
        </Skeleton>
      </ChartCard>
    );
  }

  const data = buildClientTrend(clients);

  if (data.length === 0) {
    return (
      <ChartCard>
        <div className={s.emptyChart}>
          <Icon icon={faChartLine} className={s.emptyChartIcon} />
          <p className="bf-p bfc-base-2">No clients yet</p>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <AreaChart data={data} margin={{ left: -20, right: 16, top: 8 }}>
          <defs>
            <linearGradient id="clientsTotalFill" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="var(--bfc-theme)"
                stopOpacity={0.3}
              />
              <stop
                offset="100%"
                stopColor="var(--bfc-theme)"
                stopOpacity={0}
              />
            </linearGradient>
            <linearGradient id="clientsMcpFill" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="var(--bfc-chill)"
                stopOpacity={0.3}
              />
              <stop
                offset="100%"
                stopColor="var(--bfc-chill)"
                stopOpacity={0}
              />
            </linearGradient>
            <linearGradient id="clientsAgentsFill" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--bfc-attn)" stopOpacity={0.3} />
              <stop offset="100%" stopColor="var(--bfc-attn)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="5 5"
            vertical={false}
            stroke="var(--bfc-base-dimmed)"
          />
          <XAxis
            axisLine={false}
            tickLine={false}
            dataKey="name"
            tick={{ fill: "var(--bfc-base-c-2)", fontSize: 13 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            type="number"
            tick={{ fill: "var(--bfc-base-c-2)", fontSize: 14 }}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ stroke: "var(--bfc-base-dimmed)" }}
            content={<ChartTooltip />}
          />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          <Area
            type="monotone"
            dataKey={SERIES.total}
            stroke="var(--bfc-theme)"
            strokeWidth={2}
            fill="url(#clientsTotalFill)"
          />
          <Area
            type="monotone"
            dataKey={SERIES.mcp}
            stroke="var(--bfc-chill)"
            strokeWidth={2}
            fill="url(#clientsMcpFill)"
          />
          <Area
            type="monotone"
            dataKey={SERIES.agents}
            stroke="var(--bfc-attn)"
            strokeWidth={2}
            fill="url(#clientsAgentsFill)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function StatTile({
  icon,
  label,
  value,
  to,
  warning = false,
}: {
  icon: IconDefinition;
  label: string;
  value?: number;
  to: string;
  warning?: boolean;
}) {
  return (
    <Link className={`bf-neutral-link ${s.statLink}`} to={to}>
      <div className={s.statTile}>
        <div className={s.statIcon}>
          <Icon icon={icon} />
        </div>
        <div className={s.statText}>
          <p
            className={`bf-h3 ${s.statValue} ${warning ? s.statValueWarning : ""}`}
          >
            {value !== undefined ? value : <Skeleton.Text width="2ch" />}
          </p>
          <span className={`bf-p ${s.statLabel} bf-neutral-link-text`}>
            {label}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { data: platforms } = usePlatforms();
  const { data: clients } = useClients();
  const { data: pendingRequests } = usePendingRequests();

  const ownedPlatformCount = platforms?.filter(
    (p) => p.userRole === "owner",
  ).length;
  const pendingCount = pendingRequests?.length ?? 0;

  return (
    <div className={s.pageContainer}>
      <div className={s.hero}>
        <div className={s.heroBlob} />
        <div className={s.heroContent}>
          <h1 className={`bf-h1 ${s.heroTitle}`}>Intility ID</h1>
          <p className={`bf-p ${s.heroSubtitle}`}>
            One place to administer identity and authorization for the Intility
            platform.
          </p>
        </div>
        <img
          src={heroFingerprintLight}
          alt=""
          className={`${s.heroIllustration} ${s.heroIllustrationLight}`}
        />
        <img
          src={heroFingerprintDark}
          alt=""
          className={`${s.heroIllustration} ${s.heroIllustrationDark}`}
        />
      </div>

      {pendingCount > 0 && (
        <Link className="bf-neutral-link" to="/requests">
          <Message statusBar state="warning">
            <span className="bf-neutral-link-text">
              You have{" "}
              <Badge state="warning" pill>
                {pendingCount}
              </Badge>{" "}
              pending {pendingCount === 1 ? "request" : "requests"} awaiting
              your review
            </span>
          </Message>
        </Link>
      )}

      <div className={s.statStrip}>
        <Link
          className={`bf-neutral-link ${s.statLink} ${s.statWide}`}
          to="/platforms"
        >
          <div className={s.statTile}>
            <div className={s.statIcon}>
              <Icon icon={faLayerGroup} />
            </div>
            <div className={s.statSplit}>
              <div className={s.statText}>
                <p className={`bf-h3 ${s.statValue}`}>
                  {platforms !== undefined ? (
                    platforms.length
                  ) : (
                    <Skeleton.Text width="2ch" />
                  )}
                </p>
                <span className={`bf-p ${s.statLabel} bf-neutral-link-text`}>
                  Platforms
                </span>
              </div>
              <div className={s.statDivider} />
              <div className={s.statText}>
                <p className={`bf-h3 ${s.statValue}`}>
                  {ownedPlatformCount !== undefined ? (
                    ownedPlatformCount
                  ) : (
                    <Skeleton.Text width="2ch" />
                  )}
                </p>
                <span className={`bf-p ${s.statLabel} bf-neutral-link-text`}>
                  Owned by you
                </span>
              </div>
            </div>
          </div>
        </Link>
        <StatTile
          icon={faKey}
          label="Clients"
          value={clients?.length}
          to="/clients"
        />
        <StatTile
          icon={faInbox}
          label={`Pending ${pendingCount === 1 ? "request" : "requests"}`}
          value={pendingRequests ? pendingCount : undefined}
          to="/requests"
          warning={pendingCount > 0}
        />
      </div>

      <div className={s.mainGrid}>
        <ClientsOverTimeChart clients={clients} />

        <div className={s.aside}>
          <Link
            className={`bf-neutral-link ${s.actionLink}`}
            to="/platforms/new"
          >
            <Box background="chill-fade" radius="m" className={s.actionCard}>
              <div className={s.actionIcon}>
                <Icon icon={faRocket} className="bf-large" />
              </div>
              <div className={s.actionText}>
                <span className="bf-neutral-link-text bf-h5">
                  Create a platform
                </span>
                <span className="bf-p">
                  Register a new platform with its clients and blueprint.
                </span>
              </div>
              <Box.Arrow />
            </Box>
          </Link>

          <Link
            className={`bf-neutral-link ${s.actionLink}`}
            to="/docs/getting-started/getting-started"
          >
            <Box
              background
              radius="m"
              className={`${s.actionCard} ${s.actionCardNeutral}`}
            >
              <div className={`${s.actionIcon} ${s.actionIconTheme}`}>
                <Icon icon={faBookOpen} className="bf-large" />
              </div>
              <div className={s.actionText}>
                <span className="bf-neutral-link-text bf-h5">
                  Documentation
                </span>
                <span className={`bf-p ${s.actionDescription}`}>
                  Learn how to integrate identity and authorization into your
                  app.
                </span>
              </div>
              <Box.Arrow />
            </Box>
          </Link>
        </div>
      </div>
    </div>
  );
}
